import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import {
  Box,
  COLORS,
  Flex,
  Paper,
  CircularProgress,
  LinearProgress,
  // FormHelperText,
  useMediaQuery,
  useTheme,
} from '@dripcapital/dripui-v1';
import { Link, Typography } from '@dripcapital/dripui-v1';
import { saveAs } from 'file-saver';
import Close from '@mui/icons-material/Close';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderOpen from '@mui/icons-material/FolderOpen';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import UploadFileIcon from '@mui/icons-material/Upload';
import { File as UploadFile, UploadDocument, useRemoveFileMutation } from 'api';
import { Spacer, AtomicButton, Modal } from 'atoms';
import { ButtonTypeEnum, ProgressTypeEnum } from 'enums';
import {
  DAJ_COLORS,
  DOCUMENT_TAGS,
  FONT,
  handleError,
  humanFileSize,
  renameFile,
  TOKEN,
} from 'utils';
import { useReactiveVar } from '@apollo/client';
import { appDataVar } from 'config/server';
import LinkModal from './LinkModal';

type Props = {
  buttonTitle: string;
  handleChange?: () => void;
  fileLimit: number;
  multiple: boolean;
  maxFiles?: number;
  progressType: ProgressTypeEnum;
  removeSection?: () => void;
  refetch?: () => void;
  subtitle?: string;
  supportedFiles: string[];
  title: string;
  list?: string[];
  uploads?: UploadFile[];
  url: string;
  tags?: object[];
  setLoading?: (flag: boolean) => void;
  trackEvent?: () => void;
  onClose?: () => void;
  spacing?: boolean;
  uploadError?: string;
  disabled?: boolean;
  buttonIcon?: boolean;
  /** Runs once before uploads in a multi-file selection batch (after local validation passes). */
  onBatchUploadStart?: () => void | Promise<void>;
  /** Renders between the file name and view/delete actions (e.g. digitization chip). */
  renderBeforeFileActions?: (upload: UploadFile) => React.ReactNode;
  /** Icon-only view/delete row (no “Download” / “Remove” labels). */
  iconOnlyFileActions?: boolean;
  /** Flat chrome when the upload block sits inside a parent bordered card. */
  embeddedInCard?: boolean;
  /** Header row inside card: left slot (title) + upload button aligned right — omits caption title/subtitle row. */
  embeddedHeaderStart?: React.ReactNode;
  /** Between header+bottom and file rows (bullet lists, digitization progress, subtitles). */
  belowHeaderSlot?: React.ReactNode;
  /** Runs after uploads complete and Application refetch finishes (trigger validate + digitization). */
  afterRefetchSuccess?: () => void | Promise<void>;
  /** Toolbar center: status chip etc. Between title column and upload button (default + embedded header layouts). */
  headerMiddleSlot?: React.ReactNode;
  /** Header right-top slot rendered beside/above the upload CTA. */
  headerTopSlot?: React.ReactNode;
  /** Renders inside the Paper after file rows — alerts, supplementary copy. */
  belowUploadsSlot?: React.ReactNode;
  /** Disables delete action while keeping trash icon visible. */
  disableDeleteActions?: boolean;
  /** Hides delete action icon in uploaded file rows. */
  hideDeleteActions?: boolean;
};

const Upload: React.FC<Props> = ({
  title,
  subtitle,
  list,
  buttonTitle,
  handleChange,
  multiple,
  maxFiles = 1,
  progressType,
  uploads,
  fileLimit,
  url,
  refetch,
  supportedFiles,
  tags,
  setLoading,
  trackEvent,
  onClose,
  spacing = false,
  // uploadError = '',
  disabled = false,
  buttonIcon = false,
  onBatchUploadStart,
  renderBeforeFileActions,
  iconOnlyFileActions = false,
  embeddedInCard = false,
  embeddedHeaderStart,
  belowHeaderSlot,
  afterRefetchSuccess,
  headerMiddleSlot,
  headerTopSlot,
  belowUploadsSlot,
  disableDeleteActions = false,
  hideDeleteActions = false,
}: Props) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const mdLimit = useMediaQuery(theme.breakpoints.down('md'));

  const lgLimit = useMediaQuery(theme.breakpoints.down('lg'));

  const lgLimitButton = useMediaQuery(theme.breakpoints.up('lg'));

  const [cookies] = useCookies([TOKEN]);

  const {
    applicantId,
    businessId,
    isChannelPartnerVar: isCP,
  } = useReactiveVar(appDataVar);

  const [removeFileTag] = useRemoveFileMutation();

  const [isRemoveDialogOpen, setRemoveDialog] = useState(false);

  const [currentUpload, setCurrentUpload] = useState<UploadFile>({});

  const [progress, setProgress] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [isHeaderAccordionOpen, setHeaderAccordionOpen] = useState(true);

  // const [error, setError] = useState(uploadError);

  const fileInputIdRef = useRef(
    `upload-files-${Math.random().toString(36).slice(2, 10)}`
  );

  const tax_form_information = [
    {
      title: 'C Corporation',
      subtitle:
        '(Regular corporation taxed under Subchapter C of the Internal Revenue Code)',
      form: 'Form 1120',
    },
    {
      title: 'S Corporation',
      subtitle: '(Corporation that has elected S status via Form 2553)',
      form: 'Form 1120-S',
    },
    {
      title: 'Partnership',
      subtitle: '(Includes general partnerships, LLPs, and multi-member LLCs)',
      form: 'Form 1065',
    },
    {
      title: 'Sole Proprietor',
      subtitle: '(Individual operating an unincorporated business)',
      form: 'Form 1040 + Schedule C',
    },
    {
      title: 'Estate or Trust',
      subtitle: '(Trusts and estates taxed as separate entities)',
      form: 'Form 1041',
    },
    {
      title: 'Tax-Exempt Organization / Nonprofit',
      subtitle: '(Organizations exempt under Section 501(c)(3) or similar)',
      form: 'Form 990 Series',
    },
    {
      title: 'Real Estate Investment Trust (REIT)',
      subtitle: '(Corporation that meets REIT requirements under the Code)',
      form: 'Form 1120-REIT',
    },
    {
      title: 'Regulated Investment Company (RIC)',
      subtitle:
        '(Mutual funds or similar investment vehicles taxed under special rules)',
      form: 'Form 1120-RIC',
    },
    {
      title: 'Foreign Corporation (with U.S. income)',
      subtitle:
        '(Foreign corp filing U.S. return for income effectively connected to U.S.)',
      form: 'Form 1120-F',
    },
  ];

  const isAdditionalDocs =
    url.includes(DOCUMENT_TAGS.AGEING_REPORTS) ||
    url.includes(DOCUMENT_TAGS.DEBT_PROFILE);

  // useEffect(() => {
  //   setError(uploadError);
  // }, [uploadError]);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      trackEvent && trackEvent();
      const fileUploaded = Array.from(e.target.files);
      const file = renameFile(
        fileUploaded[0],
        fileUploaded[0]?.name?.replaceAll(' ', '_')
      );
      const fileExtension = file?.name?.split('.')?.pop() || '';
      const fileSupport = supportedFiles.includes(fileExtension?.toLowerCase());
      if (!fileSupport || file.size > fileLimit) {
        let error = '';
        if (isAdditionalDocs) {
          error = t('phrase_file_upload_rule_plus_excel', {
            sizeLimit: humanFileSize(fileLimit, true, 0),
          });
        } else {
          error = t('phrase_file_upload_rule', {
            sizeLimit: humanFileSize(fileLimit, true, 0),
          });
        }
        // setError(error);
        handleError(error, {}, 8000);
      } else {
        // setError('');
        handleChange?.();
        upload(file);
      }
    }
  };

  const handleFilesSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target?.files?.length) return;
    trackEvent?.();
    const filesUploaded = Array.from(e.target?.files);
    const underscoredFiles = filesUploaded.map((file) =>
      renameFile(file, file?.name?.replaceAll(' ', '_'))
    );

    type ValidItem = { file: File };
    const toUpload: ValidItem[] = [];
    underscoredFiles.forEach((file) => {
      const fileExtension = file?.name?.split('.')?.pop() || '';
      const fileSupport = supportedFiles.includes(fileExtension?.toLowerCase());
      if (!fileSupport || file.size > fileLimit) {
        let errorMsg = '';
        if (
          url.includes(DOCUMENT_TAGS.AGEING_REPORTS) ||
          url.includes(DOCUMENT_TAGS.DEBT_PROFILE)
        ) {
          errorMsg = t('phrase_file_upload_rule_plus_excel', {
            sizeLimit: humanFileSize(fileLimit, true, 0),
          });
        } else {
          errorMsg = t('phrase_file_upload_rule', {
            sizeLimit: humanFileSize(fileLimit, true, 0),
          });
        }
        // setError(errorMsg);
        handleError(errorMsg, {}, 8000);
      } else {
        toUpload.push({ file });
      }
    });

    if (!toUpload.length) return;

    // setError('');
    try {
      await onBatchUploadStart?.();
    } catch {
      return;
    }
    toUpload.forEach(({ file }) => {
      handleChange?.();
      upload(file);
    });
  };

  const download = (upload: UploadFile) => {
    saveAs(upload?.url || '', upload?.name || '');
  };

  const openOrDownload = (upload: UploadFile) => {
    if (upload?.url) {
      window.open(upload.url, '_blank', 'noopener,noreferrer');
    } else {
      download(upload);
    }
  };

  const removeFile = async (upload: UploadFile) => {
    setLoading?.(true);
    setRemoveDialog(false);
    try {
      await removeFileTag({
        variables: {
          input: {
            documentId: upload.documentId || '',
            id: isCP ? businessId : applicantId,
          },
        },
        onError: () => {
          setRemoveDialog(false);
          handleError(t('title_file_remove_message'));
        },
      });
    } catch (e) {
      handleError(t('title_file_remove_message'));
    }
    await refetch?.();
    setLoading?.(false);
  };

  const upload = async (file: File) => {
    setLoading?.(true);
    const headers = {
      Authorization: `Bearer ${cookies[TOKEN]}`,
    };
    const formData = new FormData();
    if (tags && tags.length) {
      tags.forEach((tag: any) => {
        formData.append(tag?.key, tag.value);
      });
    }
    formData.append('file', file);
    let uploadSucceeded = false;
    try {
      await UploadDocument(url, 'POST', headers, formData, uploadProgress);
      uploadSucceeded = true;
    } catch (e) {
      handleError(t('title_file_error_message'));
    }
    if (!uploadSucceeded) {
      setLoading?.(false);
      return;
    }
    await refetch?.();
    try {
      await afterRefetchSuccess?.();
    } catch {
      /* ignore */
    }
    setLoading?.(false);
  };

  const uploadProgress = (progressEvent: any) => {
    const percentCompleted = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setProgress(percentCompleted);
  };

  const iconActionWrap = {
    cursor: 'pointer',
    width: '40px',
    height: '40px',
    borderRadius: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      background: DAJ_COLORS.WEALTHY_GREEN_50,
    },
  };

  const renderUploads = () => {
    return (
      <>
        {uploads &&
          uploads.map((upload, index) => (
            <React.Fragment key={index}>
              <Flex
                dataTestId="download-button"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                _sx={{
                  width: '100%',
                  flexWrap: iconOnlyFileActions ? 'nowrap' : 'wrap',
                  paddingX: iconOnlyFileActions ? theme.spacing(0.5) : 0,
                }}
              >
                <Flex
                  alignItems="center"
                  _sx={{
                    minWidth: 0,
                    flex: '1 1 auto',
                    paddingLeft: '34px',
                  }}
                >
                  <FolderOpen
                    sx={{
                      marginRight: 1,
                      flexShrink: 0,
                      color: COLORS.SECONDARY,
                    }}
                  />
                  <Typography
                    noWrap
                    _sx={{ maxWidth: mdLimit ? '180px' : '360px' }}
                    title={upload?.name as string}
                  >
                    {upload?.name}
                  </Typography>
                </Flex>
                <Flex
                  alignItems="center"
                  direction="row"
                  spacing={1.5}
                  _sx={{ flexShrink: 0 }}
                >
                  {renderBeforeFileActions?.(upload)}
                  {iconOnlyFileActions ? (
                    <>
                      <Flex
                        onClick={() => openOrDownload(upload)}
                        _sx={iconActionWrap}
                      >
                        <VisibilityOutlined sx={{ color: COLORS.PRIMARY }} />
                      </Flex>
                      {!hideDeleteActions ? (
                        <Flex
                          dataTestId="upload-remove-file-trigger"
                          onClick={() => {
                            if (disableDeleteActions) return;
                            setCurrentUpload(upload);
                            setRemoveDialog(true);
                          }}
                          _sx={{
                            ...iconActionWrap,
                            cursor: disableDeleteActions
                              ? 'not-allowed'
                              : 'pointer',
                            ':hover': disableDeleteActions
                              ? {}
                              : iconActionWrap[':hover'],
                          }}
                        >
                          <DeleteOutlineOutlined
                            sx={{
                              color: disableDeleteActions
                                ? DAJ_COLORS.LIGHT_GRAY
                                : COLORS.PRIMARY,
                            }}
                          />
                        </Flex>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Flex
                        dataTestId="upload-remove-file-trigger"
                        alignItems="center"
                        justifyContent={lgLimit ? 'normal' : 'flex-end'}
                        _sx={{
                          cursor: disableDeleteActions
                            ? 'not-allowed'
                            : 'pointer',
                          width: 'fit-content',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          ':hover': {
                            background: disableDeleteActions
                              ? 'transparent'
                              : DAJ_COLORS.WEALTHY_GREEN_50,
                          },
                        }}
                        onClick={() => openOrDownload(upload)}
                      >
                        <VisibilityOutlined sx={{ color: COLORS.PRIMARY }} />
                      </Flex>
                      {!hideDeleteActions ? (
                        <Flex
                          alignItems="center"
                          justifyContent={lgLimit ? 'normal' : 'flex-end'}
                          _sx={{
                            cursor: disableDeleteActions
                              ? 'not-allowed'
                              : 'pointer',
                            width: 'fit-content',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            ':hover': {
                              background: disableDeleteActions
                                ? 'transparent'
                                : DAJ_COLORS.WEALTHY_GREEN_50,
                            },
                          }}
                          onClick={() => {
                            if (disableDeleteActions) return;
                            setCurrentUpload(upload);
                            setRemoveDialog(true);
                          }}
                        >
                          <DeleteOutlineOutlined
                            sx={{
                              color: disableDeleteActions
                                ? DAJ_COLORS.LIGHT_GRAY
                                : COLORS.PRIMARY,
                            }}
                          />
                        </Flex>
                      ) : null}
                    </>
                  )}
                </Flex>
              </Flex>
            </React.Fragment>
          ))}
      </>
    );
  };

  const renderProgress = () => {
    switch (progressType) {
      case ProgressTypeEnum.CIRCULAR:
        return (
          <Box
            _sx={{
              position: 'relative',
              display: 'inline-flex',
              marginLeft: 1,
            }}
          >
            <CircularProgress
              variant="determinate"
              value={progress}
              size={42}
            />
            <Box
              _sx={{
                width: 40,
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="label" color="primary">
                {progress && progress > 0 ? progress : null}
              </Typography>
            </Box>
          </Box>
        );
      case ProgressTypeEnum.LINEAR:
        return (
          <>
            <Spacer verticalSpace={1} />
            <LinearProgress variant="determinate" value={progress} />
          </>
        );
    }
  };

  return (
    <Paper
      dataTestId="file-upload-element"
      variant="outlined"
      elevation={0}
      _sx={{
        position: 'relative',
        paddingY: embeddedInCard ? 0 : 2,
        paddingX: embeddedInCard ? 0 : 2,
        borderRadius: embeddedInCard ? 0 : '8px',
        borderWidth: embeddedInCard ? 0 : 1,
        boxShadow: embeddedInCard ? 'none !important' : undefined,
        backgroundColor: embeddedInCard ? 'transparent !important' : undefined,
        borderColor: embeddedInCard
          ? 'transparent'
          : disabled
          ? DAJ_COLORS.NEUTRAL_DARK_GREY
          : DAJ_COLORS.SKY_BLUE_400,
      }}
    >
      <Box id="upload-atoms">
        {uploads && uploads?.length == 0 && onClose && (
          <Close
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              cursor: 'pointer',
              color: DAJ_COLORS.GREEN,
            }}
          />
        )}
        {embeddedInCard && embeddedHeaderStart != null ? (
          <>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              _sx={{ width: '100%', flexWrap: 'wrap' }}
            >
              <Box _sx={{ flex: '1 1 auto', minWidth: 0 }}>
                {embeddedHeaderStart}
              </Box>
              {headerTopSlot ? (
                <Flex alignItems="center">
                  {headerTopSlot ? (
                    <Box _sx={{ marginTop: 1 }}>{headerTopSlot}</Box>
                  ) : null}
                </Flex>
              ) : null}
              {buttonTitle ? (
                <Flex
                  _sx={{ flexShrink: 0 }}
                  direction="row"
                  alignItems="flex-end"
                >
                  {progress &&
                  progress > 0 &&
                  progress < 100 &&
                  progressType === ProgressTypeEnum.CIRCULAR
                    ? renderProgress()
                    : undefined}
                  {buttonTitle ? (
                    <AtomicButton
                      variant={ButtonTypeEnum.OUTLINED}
                      disabled={
                        disabled ||
                        (progress && progress > 0 && progress < 100) ||
                        (uploads && uploads.length >= maxFiles)
                          ? true
                          : false
                      }
                      endIcon={buttonIcon ? <UploadFileIcon /> : <></>}
                      _sx={{
                        visibility: 'initial',
                        paddingX: lgLimitButton ? 6 : 1,
                        paddingY: 1,
                      }}
                      onClick={() => 1}
                      component={'label'}
                    >
                      {buttonTitle}
                      <input
                        id={fileInputIdRef.current}
                        data-testid={`${fileInputIdRef.current}-input`}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={
                          multiple ? handleFilesSelected : handleFileSelected
                        }
                        multiple={multiple}
                        autoComplete="off"
                        hidden
                      />
                    </AtomicButton>
                  ) : null}
                </Flex>
              ) : null}
            </Flex>
            {url.includes(DOCUMENT_TAGS.TAX_RETURNS) && subtitle ? (
              <>
                <Spacer verticalSpace={0.5} />
                <Link
                  _sx={{
                    fontSize: FONT.SIZES.F14,
                  }}
                  onClick={() => setOpenModal(true)}
                >
                  {subtitle}
                </Link>
              </>
            ) : null}
            {belowHeaderSlot}
            {spacing ? <Spacer verticalSpace={3} /> : null}
          </>
        ) : (
          <>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              _sx={{ width: '100%', flexWrap: 'wrap' }}
            >
              <Flex
                alignItems="center"
                spacing={1}
                _sx={{ flex: '1 1 260px', minWidth: 0 }}
              >
                <ExpandMore
                  sx={{
                    color: DAJ_COLORS.WEALTHY_GREEN,
                    fontSize: 26,
                    flexShrink: 0,
                    transform: isHeaderAccordionOpen
                      ? 'rotate(0deg)'
                      : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                    cursor: 'pointer',
                  }}
                  onClick={() => setHeaderAccordionOpen((prev) => !prev)}
                  aria-hidden
                />
                <Box _sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  <Box
                    _sx={{ width: 'fit-content', cursor: 'pointer' }}
                    onClick={() => setHeaderAccordionOpen((prev) => !prev)}
                  >
                    <Typography
                      variant="caption"
                      color="secondary"
                      fontWeight="bold"
                      _sx={{
                        lineHeight: 1.25,
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </Box>
              </Flex>

              <Flex alignItems="center" spacing={2}>
                {headerTopSlot ? <Box>{headerTopSlot}</Box> : null}
                {buttonTitle ? (
                  <Flex
                    justifyContent={'flex-end'}
                    _sx={{ flexShrink: 0 }}
                    direction="row"
                    alignItems="flex-end"
                  >
                    {progress &&
                    progress > 0 &&
                    progress < 100 &&
                    progressType === ProgressTypeEnum.CIRCULAR
                      ? renderProgress()
                      : undefined}
                    <Box _sx={{ width: '8px' }}></Box>
                    {buttonTitle ? (
                      <AtomicButton
                        variant={ButtonTypeEnum.OUTLINED}
                        disabled={
                          disabled ||
                          (progress && progress > 0 && progress < 100) ||
                          (uploads && uploads.length >= maxFiles)
                            ? true
                            : false
                        }
                        endIcon={buttonIcon ? <UploadFileIcon /> : <></>}
                        _sx={{
                          visibility: 'initial',
                          paddingX: lgLimitButton ? 6 : 1,
                          paddingY: 1,
                          height: '40px',
                        }}
                        onClick={() => 1}
                        component={'label'}
                      >
                        {buttonTitle}
                        <input
                          id={fileInputIdRef.current}
                          data-testid={`${fileInputIdRef.current}-input`}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={
                            multiple ? handleFilesSelected : handleFileSelected
                          }
                          multiple={multiple}
                          autoComplete="off"
                          hidden
                        />
                      </AtomicButton>
                    ) : null}
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
            <Flex justifyContent="flex-start">
              <Box
                _sx={{
                  width: '34px',
                }}
              />
              {isHeaderAccordionOpen ? (
                <Flex direction="column">
                  {subtitle && <Spacer verticalSpace={1} />}
                  {url.includes(DOCUMENT_TAGS.TAX_RETURNS) ? (
                    <>
                      {subtitle && (
                        <Link
                          _sx={{
                            fontSize: FONT.SIZES.F14,
                          }}
                          onClick={() => setOpenModal(true)}
                        >
                          {subtitle}
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      {subtitle && (
                        <Typography variant="label">{subtitle}</Typography>
                      )}
                    </>
                  )}
                  {list && Array.from(list) && list.length > 0 && (
                    <ul style={{ margin: '4px', paddingLeft: '16px' }}>
                      {list.map((item, index) => {
                        return (
                          <li key={index}>
                            <Typography variant="label">{item}</Typography>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {url.includes(DOCUMENT_TAGS.DEBT_PROFILE) && (
                    <AtomicButton
                      variant={ButtonTypeEnum.TEXT}
                      _sx={{
                        visibility: 'initial',
                        paddingX: lgLimitButton ? 2 : 1,
                        paddingY: 1,
                        marginLeft: 1,
                        '.MuiTypography-root': {
                          fontSize: '12px',
                        },
                        '.MuiIcon': {
                          color: COLORS.PRIMARY,
                        },
                        width: '200px',
                      }}
                      onClick={() => {
                        window.open(
                          `${process.env.PUBLIC_URL}/Debt_Profile_Sample.xlsx`,
                          '_blank'
                        );
                      }}
                      endIcon={DownloadOutlined}
                    >
                      <Typography
                        variant="body"
                        color="primary"
                        fontWeight="bold"
                      >
                        {t('usoa_fd_debt_template')}
                      </Typography>
                    </AtomicButton>
                  )}
                  {spacing ? <Spacer verticalSpace={3} /> : null}
                </Flex>
              ) : null}
            </Flex>
          </>
        )}

        {!!uploads?.length && <Spacer verticalSpace={2} />}

        {/* Uploaded documents */}
        {renderUploads()}

        {progress &&
        progress > 0 &&
        progress < 100 &&
        progressType === ProgressTypeEnum.LINEAR
          ? renderProgress()
          : null}

        {headerMiddleSlot ? (
          <Flex
            alignItems="center"
            _sx={{
              flexShrink: 0,
              flexGrow: 0,
              alignSelf: { xs: 'flex-start', sm: 'flex-start' },
            }}
          >
            {headerMiddleSlot}
          </Flex>
        ) : null}

        {belowUploadsSlot ? <>{belowUploadsSlot}</> : null}

        {/* {error && (
          <FormHelperText sx={{ color: DAJ_COLORS.RED }}>
            {error}
          </FormHelperText>
        )} */}
      </Box>
      <Modal
        dataTestId="person-info-modal"
        isOpen={isRemoveDialogOpen}
        closeDialog={() => setRemoveDialog(false)}
        title={t('phrase_remove_file')}
        content={t('phrase_remove_file_content')}
        buttons={[
          {
            dataTestId: 'accept-button',
            title: t('btn_remove'),
            handler: () => removeFile(currentUpload),
            variant: ButtonTypeEnum.CONTAINED,
            _sx: { padding: theme.spacing(1.3) },
          },
          {
            dataTestId: 'deny-button',
            title: t('btn_cancel'),
            handler: () => setRemoveDialog(false),
            variant: ButtonTypeEnum.OUTLINED,
            _sx: { padding: theme.spacing(1.3) },
          },
        ]}
        _sx={{ padding: theme.spacing(3), maxWidth: 500 }}
      />
      <LinkModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        tax_form_information={tax_form_information}
      />
    </Paper>
  );
};

export default Upload;
